/* ==========================================================================
   GOODS SCENE -- STATUS EXPLANATION POPOVERS

   Two independent disclosures on the PDP status row: Part Origin and
   Availability. Explanatory UI only -- it reads nothing, decides nothing and
   changes no product state.

   🛑 assets/global.js IS NOT TOUCHED. This is a standalone custom element.

   ===========================================================================
   🛑 THE AVAILABILITY TRIGGER IS A DISPOSABLE NODE. NOTHING MAY HOLD A REFERENCE
   TO IT, AND NOTHING MAY BIND A LISTENER TO IT.

   VariantSelects.renderProductInfo() replaces the innerHTML of
   #Inventory-{section} on EVERY variant change. The <gs-status-popover> host is
   outside that node and survives, so connectedCallback() does NOT run again --
   but the trigger <button> inside is destroyed and rebuilt.

   An earlier build cached `this.trigger` and bound `click` directly to it. That
   failed three ways after one variant change: the new button had no listener;
   aria-expanded kept being written to the DETACHED node, lying to screen readers;
   and isOpen read that detached node, so a popover left open stayed visibly open
   while the new button reported closed.

   🔑 THE RULES THAT KEEP IT FIXED:
     - `trigger` and `panel` are GETTERS, never cached fields.
     - EVERY listener is bound to the HOST, which is never replaced. Click is
       delegated with closest().
     - OPEN STATE LIVES ON THE PANEL, which is outside the replaced node.
       aria-expanded is a mirror written onto the current trigger, never read
       back as state.
     - variantChange closes the popover, so the rebuilt button and the surviving
       panel can never disagree.

   ⚠️ NO MutationObserver. Dawn publishes variantChange AFTER the swap, so the
   theme's own pubsub gives the exact signal at the exact moment.
   ===========================================================================

   ===========================================================================
   🛑 WHY focusin CHECKS :focus-visible -- THE ONE-TAP BUG.

   A tap on a <button> fires, in order:

       pointerdown -> focus -> focusin -> click

   so an unconditional `focusin -> open()` opened the panel and the click that
   followed immediately toggled it shut. The popover looked like it needed TWO
   taps: the second tap found the button already focused, fired no focusin, and
   only then did click open it.

   🔑 :focus-visible IS THE BROWSER'S OWN ANSWER TO "WAS THIS FOCUS KEYBOARD-
   INITIATED?". Per spec a <button> focused by pointer does NOT match it, while
   Tab does -- so keyboard users keep focus-to-open and pointer users get a clean
   single toggle through click. No flags, no timers, no synthetic-click
   suppression window.

   ⚠️ THE FALLBACK REUSES DAWN'S OWN POLYFILL. global.js runs
   focusVisiblePolyfill() when the selector is unsupported, which adds a
   `.focused` class on keyboard focus. matches() throws on an unknown selector in
   those browsers, so the try/catch falls through to that class rather than
   silently disabling keyboard opening.
   ===========================================================================

   🔑 ONE CHANNEL, NOT A REGISTRY. Opening broadcasts on document; every other
   instance closes itself.
   ========================================================================== */

(function () {
  if (customElements.get('gs-status-popover')) return;

  var OPEN_EVENT = 'gs-status-popover:open';

  class GsStatusPopover extends HTMLElement {
    /* 🛑 GETTERS, NOT CACHED FIELDS. Caching the trigger is the bug this element
       was rewritten to remove. Do not "optimise" these back into
       connectedCallback assignments. */
    get trigger() {
      return this.querySelector('[data-gs-pop-trigger]');
    }

    get panel() {
      return this.querySelector('[data-gs-pop-panel]');
    }

    /* 🔑 STATE LIVES ON THE PANEL -- it is rendered OUTSIDE #Inventory-* and
       survives every variant change; the trigger does not. */
    get isOpen() {
      var panel = this.panel;
      return !!panel && !panel.hidden;
    }

    connectedCallback() {
      if (!this.trigger || !this.panel) return;

      this.onDocOpen = this.onDocOpen.bind(this);
      this.onDocPointer = this.onDocPointer.bind(this);

      /* 🛑 DELEGATED FROM THE HOST. closest() matches whichever trigger exists at
         click time, so a rebuilt button needs no reinitialisation. */
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('keydown', this.onKeydown.bind(this));

      /* ⚠️ HOVER IS BOUND ONLY WHERE HOVER EXISTS. On a touch device the synthetic
         mouse events after a tap would re-close what the tap just opened. */
      if (window.matchMedia('(hover: hover)').matches) {
        this.addEventListener('mouseenter', this.open.bind(this));
        this.addEventListener('mouseleave', this.close.bind(this));
      }

      /* Keyboard focus opens it -- but ONLY keyboard focus. See the header note. */
      this.addEventListener('focusin', this.onFocusIn.bind(this));

      /* focusout fires BEFORE the next element is focused, so relatedTarget is
         checked rather than document.activeElement. */
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.close();
      });

      document.addEventListener(OPEN_EVENT, this.onDocOpen);

      /* 🔑 CLOSE ON VARIANT CHANGE -- the only thing preventing the rebuilt button
         and the surviving panel from disagreeing. ⚠️ Guarded: pubsub.js is loaded
         globally by theme.liquid, but this file must degrade rather than throw if
         that ever changes. */
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => this.close());
      }
    }

    disconnectedCallback() {
      document.removeEventListener(OPEN_EVENT, this.onDocOpen);
      document.removeEventListener('pointerdown', this.onDocPointer);
      if (this.variantUnsubscriber) this.variantUnsubscriber();
    }

    /* Returns true only when the browser considers this focus keyboard-driven.
       ⚠️ matches(':focus-visible') THROWS on browsers without the selector, which
       is exactly where Dawn's focusVisiblePolyfill() is active -- so the catch
       falls through to the `.focused` class that polyfill adds. */
    isKeyboardFocus(element) {
      if (!element || typeof element.matches !== 'function') return false;
      try {
        return element.matches(':focus-visible');
      } catch (error) {
        return !!element.classList && element.classList.contains('focused');
      }
    }

    onFocusIn(event) {
      if (!this.isKeyboardFocus(event.target)) return;
      this.open();
    }

    onClick(event) {
      var trigger = event.target.closest('[data-gs-pop-trigger]');
      if (!trigger || !this.contains(trigger)) return;
      event.preventDefault();
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    /* Enter and Space are native on a <button>, so only Escape needs handling.
       Focus returns to the CURRENT trigger, resolved through the getter. */
    onKeydown(event) {
      if (event.key !== 'Escape' || !this.isOpen) return;
      this.close();
      var trigger = this.trigger;
      if (trigger) trigger.focus();
    }

    onDocOpen(event) {
      if (event.detail !== this) this.close();
    }

    onDocPointer(event) {
      if (!this.contains(event.target)) this.close();
    }

    /* aria-expanded is written onto whatever trigger exists RIGHT NOW. It mirrors
       the panel; it is never read back as state. */
    syncTrigger(expanded) {
      var trigger = this.trigger;
      if (trigger) trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    open() {
      if (this.isOpen) return;
      this.panel.hidden = false;
      this.syncTrigger(true);
      document.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: this }));
      /* Bound only while open, so a page with everything closed carries no
         document-level pointer listeners at all. */
      document.addEventListener('pointerdown', this.onDocPointer);
    }

    close() {
      if (!this.isOpen) return;
      this.panel.hidden = true;
      this.syncTrigger(false);
      document.removeEventListener('pointerdown', this.onDocPointer);
    }
  }

  customElements.define('gs-status-popover', GsStatusPopover);
})();
